// apps/web/src/components/UpdateNotification.tsx

import { Box, Button, Slide, Typography, IconButton } from '@mui/material';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import CloseIcon from '@mui/icons-material/Close';
import { useVersionCheck } from '@/hooks/useVersionCheck';
import { useState } from 'react';

/**
 * UpdateNotification Component
 * Shows a banner when a new version of the app is available.
 * Follows the same styling pattern as CookieConsent.
 */
export function UpdateNotification() {
  const { updateAvailable, newVersion, dismissUpdate, refresh } = useVersionCheck();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refresh();
    // Note: Page will reload, so we don't need to reset state
  };

  return (
    <Slide direction="up" in={updateAvailable} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          bottom: { xs: 70, sm: 24 }, // Above mobile nav on small screens
          left: { xs: 16, sm: 24 },
          right: { xs: 16, sm: 'auto' },
          maxWidth: { sm: 420 },
          backgroundColor: 'background.paper',
          border: '1px solid #233554',
          borderRadius: 2,
          p: 2,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          boxShadow: '0 10px 30px -15px rgba(2, 12, 27, 0.7)',
          zIndex: 1400, // Below CookieConsent (9999) - legal consent takes priority over update notification
        }}
      >
        <SystemUpdateAltIcon
          sx={{
            color: 'success.main',
            fontSize: 28,
            mt: 0.5,
          }}
        />

        <Box sx={{ flex: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{
              color: 'text.primary',
              fontWeight: 600,
              mb: 0.5,
            }}
          >
            New Version Available
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mb: 2,
            }}
          >
            {newVersion
              ? `A new version from ${new Date(newVersion.buildTime).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })} is ready with the latest features and fixes.`
              : 'A new version is ready. Refresh to get the latest updates.'}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleRefresh}
              disabled={isRefreshing}
              sx={{
                backgroundColor: 'success.main',
                color: 'background.paper',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#4cd9c4',
                },
                '&:disabled': {
                  backgroundColor: '#64ffda80',
                  color: '#0a192f80',
                },
              }}
            >
              {isRefreshing ? 'Refreshing...' : 'Refresh Now'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={dismissUpdate}
              sx={{
                borderColor: '#233554',
                color: 'text.secondary',
                textTransform: 'none',
                '&:hover': {
                  borderColor: 'success.main',
                  color: 'success.main',
                  backgroundColor: 'transparent',
                },
              }}
            >
              Later
            </Button>
          </Box>
        </Box>

        <IconButton
          size="small"
          onClick={dismissUpdate}
          sx={{
            color: 'text.secondary',
            p: 0.5,
            '&:hover': {
              color: 'success.main',
              backgroundColor: 'transparent',
            },
          }}
          aria-label="Close update notification"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </Slide>
  );
}
